import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Archive, Trash2, ArchiveRestore, Loader2, Search, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { refreshBrands } from '../utils/brands';
import { getBrandBadgeClasses } from '../utils/brandColors';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrandManagement = () => {
  const { token } = useAuth();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(null); // brand object or null
  const [newName, setNewName] = useState('');
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const loadBrands = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/brands`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBrands(res.data.brands || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load brands');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) loadBrands();
  }, [token, loadBrands]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error('Brand name is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/brands`, { name }, authHeader);
      toast.success(`Brand "${name}" created`);
      setShowAdd(false);
      setNewName('');
      await loadBrands();
      await refreshBrands(token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create brand');
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async () => {
    if (!showEdit) return;
    const name = editName.trim();
    if (!name || name === showEdit.name) {
      setShowEdit(null);
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/brands/${showEdit.id}`, { name }, authHeader);
      toast.success(`Renamed to "${name}"`);
      setShowEdit(null);
      await loadBrands();
      await refreshBrands(token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rename brand');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (brand) => {
    try {
      await axios.put(
        `${API}/admin/brands/${brand.id}`,
        { is_active: !brand.is_active },
        authHeader
      );
      toast.success(brand.is_active ? 'Brand archived' : 'Brand activated');
      await loadBrands();
      await refreshBrands(token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update brand');
    }
  };

  const handleDelete = async (brand) => {
    if (!window.confirm(`Delete "${brand.name}" permanently? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/brands/${brand.id}`, authHeader);
      toast.success(`Brand "${brand.name}" deleted`);
      await loadBrands();
      await refreshBrands(token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete brand');
    }
  };

  const filtered = brands.filter((b) =>
    b.name.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: brands.length,
    active: brands.filter((b) => b.is_active).length,
    archived: brands.filter((b) => !b.is_active).length,
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Brand Management</h1>
          <p className="text-sm text-slate-500 mt-1">
            Create, rename and archive brands. Changes apply instantly across Tasks, Reports, and Social Media.
          </p>
        </div>
        <Button
          onClick={() => setShowAdd(true)}
          className="btn-cta hover:opacity-90"
          data-testid="add-brand-btn"
        >
          <Plus size={16} className="mr-2" />
          Add Brand
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Total</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Active</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{stats.active}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Archived</p>
          <p className="text-2xl font-bold text-slate-400 mt-1">{stats.archived}</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg p-4 border border-slate-200 mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search brands..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="brand-search"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-slate-500">
            <Loader2 className="animate-spin mr-2" size={18} />
            Loading brands...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">No brands found</div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Brand
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Tasks
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Users
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((brand) => (
                <tr
                  key={brand.id}
                  data-testid={`brand-row-${brand.id}`}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block px-2.5 py-1 rounded-md text-xs font-semibold border ${getBrandBadgeClasses(
                        brand.name
                      )}`}
                    >
                      {brand.name}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{brand.task_count || 0}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{brand.user_count || 0}</td>
                  <td className="px-4 py-3">
                    {brand.is_active ? (
                      <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-1 rounded">
                        Active
                      </span>
                    ) : (
                      <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-1 rounded">
                        Archived
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => {
                          setShowEdit(brand);
                          setEditName(brand.name);
                        }}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-600 hover:text-slate-900"
                        title="Rename"
                        data-testid={`brand-rename-${brand.id}`}
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        onClick={() => toggleActive(brand)}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-600 hover:text-slate-900"
                        title={brand.is_active ? 'Archive' : 'Activate'}
                        data-testid={`brand-toggle-${brand.id}`}
                      >
                        {brand.is_active ? <Archive size={15} /> : <ArchiveRestore size={15} />}
                      </button>
                      <button
                        onClick={() => handleDelete(brand)}
                        disabled={(brand.task_count || 0) + (brand.user_count || 0) > 0}
                        className="p-1.5 rounded hover:bg-red-50 text-red-500 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={
                          (brand.task_count || 0) + (brand.user_count || 0) > 0
                            ? 'Cannot delete — brand is in use. Archive instead.'
                            : 'Delete'
                        }
                        data-testid={`brand-delete-${brand.id}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Modal */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Brand</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="new-brand-name">Brand name</Label>
            <Input
              id="new-brand-name"
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. MH Vertex"
              maxLength={60}
              data-testid="new-brand-name"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
            />
            <p className="text-xs text-slate-500">Up to 60 characters. Must be unique.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="confirm-add-brand">
              {saving ? <Loader2 className="animate-spin" size={16} /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={!!showEdit} onOpenChange={(open) => !open && setShowEdit(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Brand</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="edit-brand-name">Brand name</Label>
            <Input
              id="edit-brand-name"
              autoFocus
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              maxLength={60}
              data-testid="edit-brand-name"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRename();
              }}
            />
            <p className="text-xs text-slate-500">
              Renaming cascades to all tasks, users, and social media posts using this brand.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(null)}>
              Cancel
            </Button>
            <Button onClick={handleRename} disabled={saving} data-testid="confirm-rename-brand">
              {saving ? <Loader2 className="animate-spin" size={16} /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandManagement;
